import React, { useMemo, useState } from "react";
import { DragDropContext, Draggable, Droppable, DropResult } from "@hello-pangea/dnd";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import { Close, DragIndicator } from "@mui/icons-material";
import { FormContainer, TextFieldElement, useForm } from "react-hook-form-mui";
import { useQuery } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import LegalbirdIoModal from "../Modal/LegalbirdIoModal";
import LegalbirdIoConfirm from "../Modal/LegalbirdIoConfirm";
import ButtonLoading from "../Button/ButtonLoading";
import MediaObjectLabelAndActionForm from "../MediaObject/MediaObjectLabelAndActionForm";
import MimeTypeIcon from "../MediaObject/MimeTypeIcon";
import { MediaObject } from "../../types/MediaObject";
import { ProductUnion } from "../../types/ProductUnion";
import { HydraCollectionResult } from "../../types/HydraCollectionResult";
import { AutoCompleteOptionWithValue } from "../../types/AutoCompleteOptionWithValue";
import { useCurrentUser } from "../../provider/CurrentUserProvider";
import { fetchCollection, queryKeys } from "../../services/ReactQuery/reactQueryService";
import { apiPost } from "../../services/Api/apiCall";
import { FILE_NAME_LENGTH_LIMIT, getMediaObjectFilters } from "../../services/mediaObjectService";
import {
  getMediaObjectMergeRestrictionLabel,
  reorderMediaObjectIds,
  toggleMediaObjectSelection,
} from "../../services/mediaObjectMergeService";
import { requiredFieldDefaultFieldBased } from "../../services/reactHookFormValidationRules";
import mergeMediaObjectsStyles from "./mergeMediaObjectsStyles";

type MergeMediaObjectsModalProps = {
  initialMediaObject: MediaObject;
  product: ProductUnion;
  handleClose: () => void;
  handleSuccess: () => void | Promise<void>;
};

type MergeMediaObjectFormValues = {
  originalName: string;
  documentClassLabelAi: AutoCompleteOptionWithValue | null;
  actionIdentifier: AutoCompleteOptionWithValue | null;
  description: string;
  electronicFileFolderPath: string;
  showInGallery: "true" | "false";
  sendMessageToCustomer: "true" | "false";
};

type MergeMediaObjectResponse = MediaObject & {
  sourceDeletionFailed: boolean;
};

export default function MergeMediaObjectsModal({
  initialMediaObject,
  product,
  handleClose,
  handleSuccess,
}: MergeMediaObjectsModalProps) {
  const [step, setStep] = useState<"selection" | "configuration">("selection");
  const [selectedMediaObjectIds, setSelectedMediaObjectIds] = useState<number[]>([initialMediaObject.id]);
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [pendingDeleteValues, setPendingDeleteValues] = useState<MergeMediaObjectFormValues | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const currentUser = useCurrentUser();
  const { enqueueSnackbar } = useSnackbar();
  const mediaObjectFilters = getMediaObjectFilters(product, currentUser);

  const {
    data: mediaObjectCollection,
    isLoading,
    isError,
  } = useQuery<HydraCollectionResult<MediaObject>>({
    queryKey: queryKeys.collection("media_objects", mediaObjectFilters),
    queryFn: () => fetchCollection("media_objects", mediaObjectFilters),
  });

  const mediaObjects = mediaObjectCollection?.["hydra:member"] ?? [];
  const selectedMediaObjects = useMemo(
    () =>
      selectedMediaObjectIds
        .map((mediaObjectId) => mediaObjects.find((mediaObject) => mediaObject.id === mediaObjectId))
        .filter((mediaObject): mediaObject is MediaObject => mediaObject !== undefined),
    [mediaObjects, selectedMediaObjectIds],
  );
  const selectionIsValid =
    selectedMediaObjects.length >= 2 &&
    selectedMediaObjects.every((mediaObject) => mediaObject.mergeRestriction === null);

  const formContext = useForm<MergeMediaObjectFormValues>({
    mode: "onBlur",
    defaultValues: {
      originalName: "Zusammengeführtes_Dokument.pdf",
      documentClassLabelAi: null,
      actionIdentifier: null,
      description: "Dokument",
      electronicFileFolderPath: "",
      showInGallery: "false",
      sendMessageToCustomer: "false",
    },
  });
  const handleSelectionChange = (mediaObjectId: number) => {
    setSelectedMediaObjectIds((currentSelection) => toggleMediaObjectSelection(currentSelection, mediaObjectId));
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) {
      return;
    }
    setSelectedMediaObjectIds((currentOrder) =>
      reorderMediaObjectIds(currentOrder, result.source.index, result.destination!.index),
    );
  };

  const createMergedMediaObject = async (values: MergeMediaObjectFormValues, deleteOriginals: boolean) => {
    if (!values.documentClassLabelAi || !values.actionIdentifier || !selectionIsValid) {
      return;
    }

    setIsSubmitting(true);
    const payload = new FormData();
    payload.append("type", "mediaObjectMerge");
    payload.append("product", product.productClassName);
    payload.append("productId", String(product.id));
    payload.append("originalName", values.originalName);
    payload.append("documentClassLabelAi", String(values.documentClassLabelAi.value));
    payload.append("actionIdentifier", String(values.actionIdentifier.value));
    payload.append("deleteOriginals", String(deleteOriginals));
    selectedMediaObjectIds.forEach((mediaObjectId) => {
      payload.append("sourceMediaObjectIds[]", String(mediaObjectId));
    });

    payload.append("description", values.description);
    payload.append("electronicFileFolderPath", values.electronicFileFolderPath);
    payload.append("showInGallery", values.showInGallery);
    payload.append("showInExternalView", values.showInGallery);

    let response: MergeMediaObjectResponse;
    try {
      response = await apiPost<MergeMediaObjectResponse>("media_objects", payload);
    } catch (error) {
      setIsSubmitting(false);
      enqueueSnackbar("Das zusammengeführte Dokument konnte nicht erstellt werden.", {
        variant: "error",
        isNonInteractive: true,
        persist: true,
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
      });
      return;
    }

    let notificationFailed = false;
    if (values.sendMessageToCustomer === "true") {
      try {
        await apiPost("lb/services/send_new_document_message", {
          productId: product.id,
          mediaObjectId: response.id,
        });
      } catch (error) {
        notificationFailed = true;
      }
    }

    try {
      await handleSuccess();
    } catch (error) {
      // The merge itself succeeded; a stale view must not turn the action into a retryable creation error.
    }
    setIsSubmitting(false);
    handleClose();

    let warningMessage: string | null = null;
    if (response.sourceDeletionFailed) {
      warningMessage =
        "Das neue Dokument wurde erstellt, aber nicht alle Ausgangsdokumente konnten gelöscht werden. Bitte die Dokumentenübersicht prüfen.";
    } else if (notificationFailed) {
      warningMessage = "Das neue Dokument wurde erstellt, die Mandantschaft konnte aber nicht benachrichtigt werden.";
    }
    enqueueSnackbar(warningMessage ?? "Das zusammengeführte Dokument wurde erstellt.", {
      variant: warningMessage ? "warning" : "custom",
      isNonInteractive: true,
      persist: warningMessage !== null,
      anchorOrigin: {
        vertical: "bottom",
        horizontal: "center",
      },
    });
  };

  const requestDeleteConfirmation = formContext.handleSubmit((values) => {
    setPendingDeleteValues(values);
    setDeleteConfirmationOpen(true);
  });

  const renderSelectionContent = () => {
    if (isLoading) {
      return (
        <Box sx={mergeMediaObjectsStyles.centeredContent}>
          <CircularProgress />
        </Box>
      );
    }
    if (isError) {
      return <Alert severity="error">Die Dokumente des Falls konnten nicht geladen werden.</Alert>;
    }

    return (
      <List disablePadding sx={mergeMediaObjectsStyles.selectionList}>
        {mediaObjects.map((mediaObject) => {
          const restrictionLabel = getMediaObjectMergeRestrictionLabel(mediaObject.mergeRestriction);
          const isSelected = selectedMediaObjectIds.includes(mediaObject.id);
          const labelId = `merge-media-object-${mediaObject.id}`;

          return (
            <ListItem disablePadding divider key={mediaObject.id}>
              <ListItemButton
                disabled={restrictionLabel !== null}
                onClick={() => handleSelectionChange(mediaObject.id)}
                role={undefined}
              >
                <ListItemIcon>
                  <Checkbox
                    checked={isSelected}
                    disabled={restrictionLabel !== null}
                    disableRipple
                    edge="start"
                    slotProps={{ input: { "aria-labelledby": labelId } }}
                    tabIndex={-1}
                  />
                </ListItemIcon>
                <ListItemIcon>
                  <MimeTypeIcon mimeType={mediaObject.mimeType} />
                </ListItemIcon>
                <ListItemText
                  id={labelId}
                  primary={mediaObject.originalName}
                  secondary={restrictionLabel ?? mediaObject.description}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
    );
  };

  return (
    <>
      <LegalbirdIoModal
        fullWidth
        maxWidth="md"
        open={step === "selection"}
        handleClose={handleClose}
        title="Dokumente zum Zusammenführen auswählen"
        submitButton={
          <Button variant="contained" disabled={!selectionIsValid} onClick={() => setStep("configuration")}>
            Weiter mit {selectedMediaObjectIds.length} Dokumenten
          </Button>
        }
      >
        <Typography sx={{ marginBottom: 2 }}>
          Das Dokument, über dessen Menü Sie den Vorgang gestartet haben, steht zunächst an erster Stelle. Wählen Sie
          mindestens ein weiteres Dokument aus.
        </Typography>
        {renderSelectionContent()}
      </LegalbirdIoModal>

      <LegalbirdIoModal
        fullWidth
        maxWidth="md"
        scroll="paper"
        open={step === "configuration"}
        handleClose={handleClose}
        disableBackdropClick
        title="Zusammengeführtes Dokument erstellen"
        submitButton={
          <Grid container spacing={2} sx={mergeMediaObjectsStyles.actions}>
            <Grid size={6}>
              <ButtonLoading
                fullWidth
                color="error"
                variant="outlined"
                disabled={!selectionIsValid}
                isLoading={isSubmitting}
                onClick={requestDeleteConfirmation}
              >
                Erstellen &amp; Originale löschen
              </ButtonLoading>
            </Grid>
            <Grid size={6}>
              <ButtonLoading
                fullWidth
                variant="contained"
                disabled={!selectionIsValid}
                isLoading={isSubmitting}
                onClick={formContext.handleSubmit((values) => createMergedMediaObject(values, false))}
              >
                Dokument erstellen
              </ButtonLoading>
            </Grid>
          </Grid>
        }
      >
        <Typography sx={{ marginBottom: 1 }}>Reihenfolge der Dokumente</Typography>
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="media-object-merge-order">
            {(provided) => (
              <List
                ref={provided.innerRef}
                {...provided.droppableProps}
                disablePadding
                sx={mergeMediaObjectsStyles.orderedList}
              >
                {selectedMediaObjects.map((mediaObject, index) => (
                  <Draggable draggableId={String(mediaObject.id)} index={index} key={mediaObject.id}>
                    {(draggableProvided) => (
                      <ListItem
                        ref={draggableProvided.innerRef}
                        {...draggableProvided.draggableProps}
                        divider={index < selectedMediaObjects.length - 1}
                        secondaryAction={
                          <IconButton
                            aria-label={`${mediaObject.originalName} aus der Auswahl entfernen`}
                            onClick={() => handleSelectionChange(mediaObject.id)}
                          >
                            <Close />
                          </IconButton>
                        }
                      >
                        <ListItemIcon {...draggableProvided.dragHandleProps} sx={mergeMediaObjectsStyles.dragHandle}>
                          <DragIndicator />
                        </ListItemIcon>
                        <ListItemIcon>
                          <MimeTypeIcon mimeType={mediaObject.mimeType} />
                        </ListItemIcon>
                        <ListItemText primary={mediaObject.originalName} secondary={mediaObject.description} />
                      </ListItem>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </List>
            )}
          </Droppable>
        </DragDropContext>
        <Button variant="outlined" onClick={() => setStep("selection")} sx={{ marginBottom: 2 }}>
          Weitere Dokumente auswählen
        </Button>

        <Divider sx={{ marginBottom: 2 }} />
        <FormContainer formContext={formContext}>
          <Grid container spacing={2}>
            <Grid size={12}>
              <TextFieldElement
                fullWidth
                name="originalName"
                label="Dateiname"
                rules={{
                  ...requiredFieldDefaultFieldBased,
                  maxLength: {
                    value: FILE_NAME_LENGTH_LIMIT,
                    message: `Der Dateiname darf maximal ${FILE_NAME_LENGTH_LIMIT} Zeichen lang sein`,
                  },
                  pattern: {
                    value: /\S.*\.pdf$/i,
                    message: "Der Dateiname muss mit .pdf enden",
                  },
                }}
              />
            </Grid>
            <MediaObjectLabelAndActionForm productClassName={product.productClassName} showExtraFields />
          </Grid>
        </FormContainer>
      </LegalbirdIoModal>

      <LegalbirdIoConfirm
        open={deleteConfirmationOpen}
        handleClose={() => setDeleteConfirmationOpen(false)}
        headline="Ausgangsdokumente löschen?"
        confirmText="Erstellen und Dokumente löschen"
        handleConfirm={async () => {
          if (pendingDeleteValues) {
            await createMergedMediaObject(pendingDeleteValues, true);
          }
        }}
        content={
          <>
            <Typography>
              Nach erfolgreicher Erstellung werden die folgenden Ausgangsdokumente unwiderruflich gelöscht:
            </Typography>
            <Box component="ul" sx={mergeMediaObjectsStyles.confirmationList}>
              {selectedMediaObjects.map((mediaObject) => (
                <li key={mediaObject.id}>{mediaObject.originalName}</li>
              ))}
            </Box>
          </>
        }
      />
    </>
  );
}
